/**
 * 主權地圖截圖共用引擎（僅合成技術；各入口保留自有尺寸／流程）
 */

/** 紙本輸出解析度。版面仍以 96dpi CSS 推算，輸出時放大至此。 */
export const SCREENSHOT_PRINT_DPI = 300;
export const SCREENSHOT_LAYOUT_DPI = 96;
export const SCREENSHOT_PRINT_SCALE = SCREENSHOT_PRINT_DPI / SCREENSHOT_LAYOUT_DPI;

/** @type {Record<string, { w: number, h: number, unit: 'px'|'cm'|'mm' }>} */
export const SCREENSHOT_FORMATS = {
    /** 分時人口報表地圖／一般全域 — SOVEREIGN_ARCH_GUIDELINE §3 */
    GLOBAL_CM: { w: 17.9, h: 12.1, unit: 'cm' },
    /** 截圖工作箱 Canva 畫框（輸出時乘 300/96） */
    CANVA_PX: { w: 1057, h: 714, unit: 'px' },
    /**
     * @deprecated 舊 20×5.5 cm 橫條（字級在 A4 不可讀）
     */
    LIST_CM: { w: 20, h: 5.5, unit: 'cm' },
    /**
     * 公車路線清單卡（獨立 PNG，非整頁）
     * 寬＝A4 內文寬；h＝參考最小高度（實際匯出依 pill 內容動態長高）
     */
    BUS_ROUTE_LIST_MM: { w: 190, h: 95, unit: 'mm' },
    /** 分時人口報表 · 人口表（固定比例，對齊附圖約 6.87:1） */
    POP_TABLE_CM: { w: 17.9, h: 2.6, unit: 'cm' }
};

/**
 * 公車路線清單卡版面（對齊樣張表格尺寸／字級；輸出仍是獨立圖，非整頁 A4）
 * 96dpi 排版 → × PRINT_SCALE = 300dpi（12pt ≈ 實體 4.2mm）
 * 高度：minHMm～maxHMm 隨每列 pill 換行動態夾緊（禁止固定等高＋overflow 疊字）
 */
export const BUS_ROUTE_LIST_LAYOUT = {
    wMm: 190,
    /** @deprecated 改用 minHMm；保留給舊測試／參考 */
    hMm: 95,
    minHMm: 70,
    maxHMm: 320,
    padMm: 5,
    rowPadMm: 2.2,
    minutePt: 12,
    pillPt: 11,
    border: '#94a3b8',
    rowDash: '#cbd5e1'
};

/** 清單卡高度：量測內容後夾在 min／max（layout dpi 像素） */
export function clampBusRouteListHeightPx(measuredPx, layout = BUS_ROUTE_LIST_LAYOUT, dpi = SCREENSHOT_LAYOUT_DPI) {
    const minH = mmToPx(layout.minHMm ?? layout.hMm ?? 70, dpi);
    const maxH = mmToPx(layout.maxHMm ?? 320, dpi);
    const m = Math.ceil(Number(measuredPx) || 0);
    if (!Number.isFinite(m) || m <= 0) return minH;
    return Math.min(maxH, Math.max(minH, m));
}

/**
 * 分時人口表欄寬（總框固定）：依實際字串相對寬度分配百分比。
 * label 欄夾在 labelMinPct～labelMaxPct；其餘分鐘欄按 max(表頭, 人口字) 比例分。
 * @param {{ labelTexts?: string[], headers?: string[], bodies?: string[], labelMinPct?: number, labelMaxPct?: number, measure?: (s:string)=>number }} opts
 * @returns {{ labelPct: number, colPcts: number[] }}
 */
export function allocatePopTableColPercents(opts = {}) {
    const labelTexts = opts.labelTexts?.length ? opts.labelTexts : ['步行時間', '人口'];
    const headers = Array.isArray(opts.headers) ? opts.headers : [];
    const bodies = Array.isArray(opts.bodies) ? opts.bodies : [];
    const n = Math.max(headers.length, bodies.length, 1);
    const labelMin = opts.labelMinPct ?? 12;
    const labelMax = opts.labelMaxPct ?? 16;
    const measure = typeof opts.measure === 'function'
        ? opts.measure
        : (s) => {
            const t = String(s ?? '');
            let w = 0;
            for (const ch of t) {
                if (/\d/.test(ch)) w += 0.62;
                else if (ch === ',' || ch === '.' || ch === ' ' || ch === '，') w += 0.35;
                else if (/[a-zA-Z]/.test(ch)) w += 0.55;
                else w += 1; // CJK／其餘
            }
            return Math.max(w, 0.5);
        };

    const labelNeed = Math.max(...labelTexts.map((t) => measure(t)), 1);
    let colNeeds = [];
    for (let i = 0; i < n; i++) {
        const h = measure(headers[i] || `${(i + 1) * 2}分鐘`);
        const b = measure(bodies[i] || '—');
        colNeeds.push(Math.max(h, b, 1));
    }
    const colSum = colNeeds.reduce((a, b) => a + b, 0) || 1;

    // 先估 label：相對權重，再夾上下限
    const rawLabelPct = (labelNeed / (labelNeed + colSum)) * 100;
    const labelPct = Math.min(labelMax, Math.max(labelMin, rawLabelPct));
    const rest = 100 - labelPct;
    let colPcts = colNeeds.map((w) => (w / colSum) * rest);

    // 浮點歸一，最後一欄吃誤差
    const s = colPcts.reduce((a, b) => a + b, 0) || 1;
    colPcts = colPcts.map((p) => (p / s) * rest);
    const drift = rest - colPcts.reduce((a, b) => a + b, 0);
    if (colPcts.length) colPcts[colPcts.length - 1] += drift;

    return { labelPct, colPcts };
}

export function mmToPx(mm, dpi = SCREENSHOT_PRINT_DPI) {
    return Math.round((mm / 25.4) * dpi);
}

export function ptToPx(pt, dpi = SCREENSHOT_LAYOUT_DPI) {
    return Math.round((pt / 72) * dpi);
}

/** Leaflet 由 map.ejs 以 classic script 載入，ESM 內僅能使用 window.L */
function leaflet() {
    return typeof window !== 'undefined' ? window.L : null;
}

export function cmToPx(cm, dpi = SCREENSHOT_PRINT_DPI) {
    return Math.round((cm / 2.54) * dpi);
}

export function formatPixelSize(format, dpi = SCREENSHOT_PRINT_DPI) {
    if (format.unit === 'cm') {
        return { width: cmToPx(format.w, dpi), height: cmToPx(format.h, dpi) };
    }
    if (format.unit === 'mm') {
        return { width: mmToPx(format.w, dpi), height: mmToPx(format.h, dpi) };
    }
    const scale = dpi / SCREENSHOT_LAYOUT_DPI;
    return {
        width: Math.round(format.w * scale),
        height: Math.round(format.h * scale)
    };
}

export function scaleViewportForPrint(viewport, dpi = SCREENSHOT_PRINT_DPI) {
    const scale = dpi / SCREENSHOT_LAYOUT_DPI;
    return {
        ...viewport,
        width: Math.round((viewport.width || 0) * scale),
        height: Math.round((viewport.height || 0) * scale)
    };
}

function crc32(bytes) {
    let crc = 0xffffffff;
    for (let i = 0; i < bytes.length; i += 1) {
        crc ^= bytes[i];
        for (let j = 0; j < 8; j += 1) {
            crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
        }
    }
    return (crc ^ 0xffffffff) >>> 0;
}

function writeU32(view, offset, value) {
    view.setUint32(offset, value >>> 0, false);
}

/** 寫入 PNG pHYs，讓印刷軟體認 300 dpi 實體尺寸 */
export function embedPngDpi(pngBytes, dpi = SCREENSHOT_PRINT_DPI) {
    const src = pngBytes instanceof Uint8Array ? pngBytes : new Uint8Array(pngBytes);
    if (src.length < 33 || src[0] !== 0x89 || src[1] !== 0x50) {
        throw new Error('不是有效 PNG');
    }
    const ppm = Math.round(dpi / 0.0254);
    const phys = new Uint8Array(21);
    const view = new DataView(phys.buffer);
    writeU32(view, 0, 9);
    phys[4] = 0x70; phys[5] = 0x48; phys[6] = 0x59; phys[7] = 0x73;
    writeU32(view, 8, ppm);
    writeU32(view, 12, ppm);
    phys[16] = 1;
    writeU32(view, 17, crc32(phys.subarray(4, 17)));

    const ihdrLen = 8 + 13 + 4;
    const insertAt = 8 + ihdrLen;
    const out = new Uint8Array(src.length + phys.length);
    out.set(src.subarray(0, insertAt), 0);
    out.set(phys, insertAt);
    out.set(src.subarray(insertAt), insertAt + phys.length);
    return out;
}

/**
 * 依分時圈邊界推算 ghost 視窗像素（與 ui-controls 等時圈模式一致）
 */
export function computeIsoDynamicViewport(isoBounds, map, options = {}) {
    const padding = options.padding ?? 60;
    const maxDim = options.maxDim ?? 1600;
    const minZoom = options.minZoom ?? 14;
    const maxZoom = options.maxZoom ?? 18;

    if (!isoBounds?.isValid?.() || !map) {
        return { width: 0, height: 0, zoom: 16, valid: false };
    }

    let targetZoom = minZoom;
    let width = 0;
    let height = 0;

    for (let z = maxZoom; z >= minZoom; z--) {
        const sw = map.project(isoBounds.getSouthWest(), z);
        const ne = map.project(isoBounds.getNorthEast(), z);
        const w = Math.abs(ne.x - sw.x) + padding;
        const h = Math.abs(ne.y - sw.y) + padding;
        if (w < maxDim && h < maxDim) {
            targetZoom = z;
            width = Math.round(w);
            height = Math.round(h);
            break;
        }
        if (z === minZoom) {
            targetZoom = z;
            width = Math.round(Math.min(maxDim, w));
            height = Math.round(Math.min(maxDim, h));
        }
    }

    return { width, height, zoom: targetZoom, valid: width > 0 && height > 0 };
}

/**
 * 合併多個 GeoJSON Feature 的邊界（供公車路線截圖視窗）
 */
export function boundsFromGeoFeatures(features, seedBounds = null) {
    const Lref = leaflet();
    if (!Lref || !features?.length) {
        return seedBounds?.isValid?.() ? seedBounds : null;
    }

    let bounds = seedBounds && seedBounds.isValid() ? seedBounds : null;
    for (const feat of features) {
        try {
            const layer = Lref.geoJSON(feat);
            const b = layer.getBounds();
            if (!b.isValid()) continue;
            bounds = bounds ? bounds.extend(b) : b;
        } catch {
            /* 略過 */
        }
    }
    return bounds?.isValid?.() ? bounds : null;
}

/**
 * 等待 Leaflet 底圖瓦片載入（含逾時）
 */
export function waitForMapTiles(map, timeoutMs = 4000) {
    if (!map) return Promise.resolve();

    return new Promise((resolve) => {
        let tileLayer = null;
        const Lref = leaflet();
        if (map._layers && Lref) {
            for (const id of Object.keys(map._layers)) {
                if (map._layers[id] instanceof Lref.TileLayer) {
                    tileLayer = map._layers[id];
                    break;
                }
            }
        }
        if (!tileLayer) {
            resolve();
            return;
        }

        const timer = setTimeout(() => resolve(), timeoutMs);
        const done = () => {
            clearTimeout(timer);
            // 再讓瀏覽器 paint 幾幀，減少白格破圖
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        };

        const checkIdle = () => {
            if (!tileLayer.isLoading()) done();
            else tileLayer.once('load', done);
        };

        if (!tileLayer.isLoading()) {
            setTimeout(checkIdle, 200);
        } else {
            tileLayer.once('load', checkIdle);
        }
    });
}

async function drawTileImage(ctx, img, mapRect) {
    if (!img.complete || img.naturalWidth === 0) return;
    const imgRect = img.getBoundingClientRect();
    const x = Math.round(imgRect.left - mapRect.left);
    const y = Math.round(imgRect.top - mapRect.top);
    const w = Math.round(imgRect.width);
    const h = Math.round(imgRect.height);
    if (w <= 0 || h <= 0) return;

    try {
        ctx.drawImage(img, x, y, w, h);
        return;
    } catch {
        /* canvas tainted — fetch blob fallback */
    }

    try {
        const resp = await fetch(img.src);
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        const freshImg = await new Promise((res, rej) => {
            const i = new Image();
            i.onload = () => res(i);
            i.onerror = rej;
            i.src = blobUrl;
        });
        ctx.drawImage(freshImg, x, y, w, h);
        URL.revokeObjectURL(blobUrl);
    } catch (err) {
        console.warn('[MapCapture] Tile fallback failed:', err.message);
    }
}

async function drawSvgElement(ctx, svg, mapRect) {
    // Leaflet 把 pan 寫在 SVG 的 CSS transform；getBoundingClientRect 已含位移。
    // 轉成 <img> 時 transform 行為不一致 → 剝掉 transform，用 rect 定位，避免底圖／路線不同比例。
    const svgRect = svg.getBoundingClientRect();
    const x = Math.round(svgRect.left - mapRect.left);
    const y = Math.round(svgRect.top - mapRect.top);
    const w = Math.round(svgRect.width);
    const h = Math.round(svgRect.height);
    if (w <= 0 || h <= 0) return;

    const svgClone = svg.cloneNode(true);
    svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svgClone.setAttribute('width', String(w));
    svgClone.setAttribute('height', String(h));
    // 根節點 CSS transform 已反映在 getBoundingClientRect；轉 img 時再套一次會錯位／錯比例
    svgClone.style.transform = 'none';
    svgClone.style.transformOrigin = '0 0';
    const svgStr = new XMLSerializer().serializeToString(svgClone);
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    await new Promise((resolve) => {
        const image = new Image();
        image.onload = () => {
            try { ctx.drawImage(image, x, y, w, h); } catch { /* ignore */ }
            URL.revokeObjectURL(url);
            resolve();
        };
        image.onerror = () => {
            URL.revokeObjectURL(url);
            resolve();
        };
        image.src = url;
    });
}

/**
 * 捕捉 Leaflet HTML 標記 — 禁止對 Leaflet pane 跑 html2canvas
 *（會炸 Cannot read properties of undefined (reading 'appendChild')）。
 * 只用已載入的 <img>／內嵌 SVG 直接畫上 canvas。
 */
async function drawHtmlMarkers(ctx, mapEl, mapRect, dpr, cap = 120) {
    if (!mapEl) return;

    const markerNodes = Array.from(mapEl.querySelectorAll('.leaflet-pane .leaflet-marker-icon'));
    const tipNodes = Array.from(mapEl.querySelectorAll('.leaflet-tooltip-pane .leaflet-tooltip'));
    /** @type {Map<string, HTMLCanvasElement|HTMLImageElement>} */
    const svgCache = new Map();
    let drawn = 0;

    const visibleBox = (node) => {
        const rect = node.getBoundingClientRect();
        const x = Math.round(rect.left - mapRect.left);
        const y = Math.round(rect.top - mapRect.top);
        const w = Math.round(rect.width);
        const h = Math.round(rect.height);
        if (w <= 0 || h <= 0) return null;
        if (x + w < 0 || y + h < 0 || x > mapRect.width || y > mapRect.height) return null;
        return { x, y, w, h };
    };

    const drawImg = (img, box) => {
        if (!img?.complete || !img.naturalWidth) return false;
        try {
            ctx.drawImage(img, box.x, box.y, box.w, box.h);
            drawn += 1;
            return true;
        } catch {
            return false;
        }
    };

    const rasterSvg = async (svgEl, box) => {
        const key = `${svgEl.outerHTML}|${box.w}x${box.h}`;
        let img = svgCache.get(key);
        if (!img) {
            const clone = svgEl.cloneNode(true);
            clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
            clone.setAttribute('width', String(box.w));
            clone.setAttribute('height', String(box.h));
            const blob = new Blob([new XMLSerializer().serializeToString(clone)], {
                type: 'image/svg+xml;charset=utf-8'
            });
            const url = URL.createObjectURL(blob);
            img = await new Promise((resolve) => {
                const i = new Image();
                i.onload = () => {
                    URL.revokeObjectURL(url);
                    resolve(i);
                };
                i.onerror = () => {
                    URL.revokeObjectURL(url);
                    resolve(null);
                };
                i.src = url;
            });
            if (img) svgCache.set(key, img);
        }
        if (!img) return false;
        try {
            ctx.drawImage(img, box.x, box.y, box.w, box.h);
            drawn += 1;
            return true;
        } catch {
            return false;
        }
    };

    const fillRoundRect = (x, y, w, h, r, fillStyle) => {
        if (w <= 0 || h <= 0) return;
        ctx.beginPath();
        const radius = Math.max(0, Math.min(r, w / 2, h / 2));
        if (typeof ctx.roundRect === 'function') {
            ctx.roundRect(x, y, w, h, radius);
        } else {
            ctx.moveTo(x + radius, y);
            ctx.arcTo(x + w, y, x + w, y + h, radius);
            ctx.arcTo(x + w, y + h, x, y + h, radius);
            ctx.arcTo(x, y + h, x, y, radius);
            ctx.arcTo(x, y, x + w, y, radius);
            ctx.closePath();
        }
        ctx.fillStyle = fillStyle;
        ctx.fill();
    };

    const strokeRoundRect = (x, y, w, h, r, strokeStyle, lineWidth = 1) => {
        if (w <= 0 || h <= 0) return;
        ctx.beginPath();
        const radius = Math.max(0, Math.min(r, w / 2, h / 2));
        if (typeof ctx.roundRect === 'function') {
            ctx.roundRect(x, y, w, h, radius);
        } else {
            ctx.moveTo(x + radius, y);
            ctx.arcTo(x + w, y, x + w, y + h, radius);
            ctx.arcTo(x + w, y + h, x, y + h, radius);
            ctx.arcTo(x, y + h, x, y, radius);
            ctx.arcTo(x, y, x + w, y, radius);
            ctx.closePath();
        }
        ctx.strokeStyle = strokeStyle;
        ctx.lineWidth = lineWidth;
        ctx.stroke();
    };

    /** YouBike 站點標記 (.yb-mark) */
    const drawYouBikeMarker = (node, box) => {
        const yb = node.classList?.contains('yb-mark') ? node : node.querySelector?.('.yb-mark');
        if (!yb) return false;

        const badge = yb.querySelector('.yb-badge');
        const count = yb.querySelector('.yb-count');
        if (!badge && !count) return false;

        const badgeBox = badge ? visibleBox(badge) : null;
        const countBox = count ? visibleBox(count) : null;

        if (badge) {
            const bTarget = badgeBox || {
                x: box.x,
                y: box.y,
                w: Math.min(24, Math.round(box.w * 0.45)),
                h: Math.min(18, box.h)
            };
            const gen = (badge.textContent || '').trim() || '2.0';
            const isGen1 = badge.classList.contains('gen-1');
            const isGen2 = badge.classList.contains('gen-2');
            const bg = isGen1 ? '#0f766e' : (isGen2 ? '#059669' : '#64748b');
            fillRoundRect(bTarget.x, bTarget.y, bTarget.w, bTarget.h, 3, bg);
            ctx.fillStyle = '#ffffff';
            ctx.font = `800 ${Math.max(8, Math.round(bTarget.h * 0.6))}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(gen, bTarget.x + bTarget.w / 2, bTarget.y + bTarget.h / 2);
        }

        if (count) {
            const cTarget = countBox || {
                x: box.x + Math.min(24, Math.round(box.w * 0.45)) + 2,
                y: box.y,
                w: Math.max(18, box.w - Math.min(24, Math.round(box.w * 0.45)) - 2),
                h: Math.min(18, box.h)
            };
            const numText = (count.textContent || '').trim();
            fillRoundRect(cTarget.x, cTarget.y, cTarget.w, cTarget.h, 3, 'rgba(15, 23, 42, 0.88)');
            const isCap = count.classList.contains('is-capacity');
            const isZero = count.classList.contains('is-zero');
            strokeRoundRect(
                cTarget.x, cTarget.y, cTarget.w, cTarget.h, 3,
                isCap ? 'rgba(251, 191, 36, 0.6)' : (isZero ? 'rgba(248, 113, 113, 0.6)' : 'rgba(52, 211, 153, 0.5)'),
                1
            );
            ctx.fillStyle = isCap ? '#fde68a' : (isZero ? '#fecaca' : '#ecfdf5');
            ctx.font = `700 ${Math.max(8, Math.round(cTarget.h * 0.62))}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(numText, cTarget.x + cTarget.w / 2, cTarget.y + cTarget.h / 2);
        }

        drawn += 1;
        return true;
    };

    /** 停車場標記 (.park-mark) */
    const drawParkingMarker = (node, box) => {
        const park = node.classList?.contains('park-mark') ? node : node.querySelector?.('.park-mark');
        if (!park) return false;

        const pEl = park.querySelector('.park-p');
        const pBox = pEl ? visibleBox(pEl) : null;
        const isHl = park.classList.contains('is-hl');
        const isEv = pEl?.classList.contains('park-p-ev');
        const pBg = isHl ? '#2962ff' : (isEv ? '#16a34a' : '#4a5568');

        const targetP = pBox || {
            x: box.x,
            y: box.y,
            w: Math.min(18, box.w),
            h: Math.min(18, box.h)
        };
        fillRoundRect(targetP.x, targetP.y, targetP.w, targetP.h, 3, pBg);
        ctx.fillStyle = '#ffffff';
        ctx.font = `800 ${Math.max(9, Math.round(targetP.h * 0.65))}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('P', targetP.x + targetP.w / 2, targetP.y + targetP.h / 2);

        // 附帶的徽章 badges（如 ⚡、機、月、殘）
        const badges = Array.from(park.querySelectorAll('.park-ico'));
        for (const b of badges) {
            const bBox = visibleBox(b);
            if (!bBox) continue;
            let bBg = b.style?.backgroundColor;
            if (!bBg) {
                try {
                    const computed = getComputedStyle(b);
                    bBg = computed.backgroundColor;
                } catch {
                    /* ignore */
                }
            }
            if (!bBg || bBg === 'transparent' || bBg === 'rgba(0, 0, 0, 0)') {
                bBg = '#0284c7';
            }
            fillRoundRect(bBox.x, bBox.y, bBox.w, bBox.h, 3, bBg);
            ctx.fillStyle = '#ffffff';
            ctx.font = `800 ${Math.max(8, Math.round(bBox.h * 0.6))}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText((b.textContent || '').trim().slice(0, 2), bBox.x + bBox.w / 2, bBox.y + bBox.h / 2);
        }

        drawn += 1;
        return true;
    };

    /** 獨立 EV 充電站標記 (.ev-mark 或 .custom-ev-station) */
    const drawEvMarker = (node, box) => {
        const ev = node.classList?.contains('ev-mark') ? node : node.querySelector?.('.ev-mark');
        if (!ev) return false;
        fillRoundRect(box.x, box.y, box.w, box.h, 4, '#16a34a');
        ctx.fillStyle = '#ffffff';
        ctx.font = `800 ${Math.max(9, Math.round(box.h * 0.65))}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('⚡', box.x + box.w / 2, box.y + box.h / 2);
        drawn += 1;
        return true;
    };

    /**
     * 超商／量販 (.retail-badge) — 必須對齊 public/css/retail-map-overlays.css
     * 否則 generic fallback 只畫白字（活圖有色底、截圖變白字）。
     */
    const RETAIL_BRAND_COLORS = {
        '711': { bg: '#16a34a', fg: '#ffffff' },
        FAMILYMART: { bg: '#2563eb', fg: '#ffffff' },
        HILIFE: { bg: '#ea580c', fg: '#ffffff' },
        OKMART: { bg: '#e11d48', fg: '#ffffff' },
        PXMART: { bg: '#fbbf24', fg: '#0f172a' }
    };
    const drawRetailMarker = (node, box) => {
        const mark = node.classList?.contains('retail-mark')
            ? node
            : node.querySelector?.('.retail-mark');
        const badge = mark?.querySelector?.('.retail-badge')
            || node.querySelector?.('.retail-badge');
        if (!badge) return false;

        const bBox = visibleBox(badge) || {
            x: box.x,
            y: box.y,
            w: Math.max(22, Math.min(48, box.w)),
            h: Math.max(14, Math.min(18, box.h))
        };

        let brandId = '';
        for (const c of badge.classList || []) {
            const name = String(c || '');
            if (name.startsWith('brand-')) {
                brandId = name.slice(6).toUpperCase();
                break;
            }
        }
        const preset = RETAIL_BRAND_COLORS[brandId];
        let bg = preset?.bg || '';
        let fg = preset?.fg || '';
        if (!bg || !fg) {
            try {
                const cs = getComputedStyle(badge);
                if (!bg) bg = cs.backgroundColor;
                if (!fg) fg = cs.color;
            } catch {
                /* ignore */
            }
        }
        if (!bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)') bg = '#fbbf24';
        if (!fg || fg === 'transparent' || fg === 'rgba(0, 0, 0, 0)') fg = '#0f172a';

        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 2;
        ctx.shadowOffsetY = 1;
        fillRoundRect(bBox.x, bBox.y, bBox.w, bBox.h, 3, bg);
        ctx.restore();

        const text = (badge.textContent || '').trim();
        if (text) {
            ctx.fillStyle = fg;
            ctx.font = `800 ${Math.max(8, Math.round(bBox.h * 0.62))}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(text, bBox.x + bBox.w / 2, bBox.y + bBox.h / 2 + 0.5);
        }
        drawn += 1;
        return true;
    };

    /** 通用自訂 HTML 標記 fallback（公車站牌、出入口等） */
    const drawGenericHtmlMarker = (node, box) => {
        const text = (node.textContent || '').trim();
        if (!text && !node.children.length) return false;
        let bg = node.style?.backgroundColor;
        let color = node.style?.color;
        if (!bg || !color) {
            try {
                const style = getComputedStyle(node);
                bg = bg || style.backgroundColor;
                color = color || style.color;
            } catch {
                /* ignore */
            }
        }
        const hasBg = bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent';
        if (hasBg) {
            fillRoundRect(box.x, box.y, box.w, box.h, 4, bg);
        }
        if (text && text.length <= 10) {
            ctx.fillStyle = (color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent') ? color : '#ffffff';
            ctx.font = `bold ${Math.max(8, Math.round(box.h * 0.5))}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(text, box.x + box.w / 2, box.y + box.h / 2);
            drawn += 1;
            return true;
        }
        if (hasBg) {
            drawn += 1;
            return true;
        }
        return false;
    };

    /** 捷運／軌道站：divIcon 內嵌路線色同心圓 SVG（全機能截圖必畫） */
    const drawRailStationMarker = async (node, box) => {
        const cls = String(node.className || '');
        if (!cls.includes('rail-station-marker')) return false;
        const svg = node.querySelector?.('svg');
        if (!svg) return false;
        return rasterSvg(svg, box);
    };

    /** 圓底色 + 白線圖（一般 POI） */
    const drawPoiMarkerDiv = async (node, box) => {
        const wrap = node.querySelector?.('.poi-marker, .poi-cluster, .poi-brand-img');
        if (!wrap) return false;
        const bg = wrap.style?.background || getComputedStyle(wrap).backgroundColor || '#666';
        const r = Math.min(box.w, box.h) / 2;
        const cx = box.x + box.w / 2;
        const cy = box.y + box.h / 2;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = bg;
        ctx.fill();
        const img = wrap.querySelector?.('img');
        if (img && drawImg(img, {
            x: box.x + box.w * 0.15,
            y: box.y + box.h * 0.15,
            w: box.w * 0.7,
            h: box.h * 0.7
        })) return true;
        const svg = wrap.querySelector?.('svg');
        if (svg) {
            const pad = Math.max(2, Math.round(box.w * 0.18));
            await rasterSvg(svg, {
                x: box.x + pad,
                y: box.y + pad,
                w: box.w - pad * 2,
                h: box.h - pad * 2
            });
            return true;
        }
        // 純文字（cluster 數字／品牌字）
        const text = (wrap.textContent || '').trim();
        if (text) {
            ctx.fillStyle = '#fff';
            ctx.font = `bold ${Math.max(9, Math.round(box.h * 0.45))}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(text.slice(0, 4), cx, cy);
            drawn += 1;
            return true;
        }
        drawn += 1;
        return true;
    };

    for (const node of markerNodes) {
        if (drawn >= cap) break;
        const box = visibleBox(node);
        if (!box) continue;
        const img = node.tagName === 'IMG' ? node : node.querySelector?.('img');
        if (img && !node.querySelector?.('.poi-marker, .poi-brand-img') && drawImg(img, box)) continue;
        if (await drawRailStationMarker(node, box)) continue;
        if (await drawPoiMarkerDiv(node, box)) continue;
        if (drawRetailMarker(node, box)) continue;
        if (drawYouBikeMarker(node, box)) continue;
        if (drawParkingMarker(node, box)) continue;
        if (drawEvMarker(node, box)) continue;
        if (img && drawImg(img, box)) continue;
        // 其他僅含 SVG 的 HTML 標記（後備）
        const loneSvg = node.querySelector?.('svg');
        if (loneSvg && !node.querySelector?.('img') && (await rasterSvg(loneSvg, box))) continue;
        if (drawGenericHtmlMarker(node, box)) continue;
    }

    // tooltip：字少，canvas 直接畫文字（不走 html2canvas）
    for (const node of tipNodes) {
        if (drawn >= cap) break;
        const box = visibleBox(node);
        if (!box) continue;
        const text = (node.textContent || '').trim();
        if (!text) continue;
        ctx.fillStyle = 'rgba(15,23,42,0.85)';
        ctx.fillRect(box.x, box.y, box.w, box.h);
        ctx.fillStyle = '#fff';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(text.slice(0, 40), box.x + 4, box.y + box.h / 2);
        drawn += 1;
    }

    if (drawn > 0) {
        console.info(`[MapCapture] HTML markers native-drawn=${drawn} (no html2canvas)`);
    }
}

/**
 * 從作用中地圖推斷底圖 URL（供 ghost 截圖）
 */
export function getActiveTileLayerUrl(map) {
    const Lref = leaflet();
    if (!map?._layers || !Lref) {
        return '/api/tiles/carto/{z}/{x}/{y}.png?v=17.0.796';
    }
    for (const id of Object.keys(map._layers)) {
        const layer = map._layers[id];
        if (layer instanceof Lref.TileLayer && layer._url) {
            return layer._url;
        }
    }
    return '/api/tiles/carto/{z}/{x}/{y}.png?v=17.0.796';
}

/** 分時人口報表地圖強制淺色交通底圖（不跟活地圖目前底圖） */
export const LIGHT_TRANSPORT_TILE_URL = '/api/tiles/transport/{z}/{x}/{y}.png?v=17.0.796';

/** 公車報表／POI：Carto Light（非 Thunderforest） */
export const LIGHT_CARTO_TILE_URL = '/api/tiles/carto/{z}/{x}/{y}.png?v=17.0.796';

/**
 * 人口表表頭樣式（樣張 image 逐格取樣 · 不得另造色）
 * bg：步行時間／2分鐘 #febd59；4–12 #1f3356
 * 字：步行時間黑；2–12 見 minuteText
 */
export const POP_TABLE_HEADER_STYLE = {
    labelBg: '#febd59',
    labelColor: '#000000',
    earlyBg: '#febd59',
    lateBg: '#1f3356',
    border: '#000000',
    bodyBg: '#ffffff',
    bodyColor: '#000000',
    /** @type {Record<number, string>} minutes → 表頭文字色（樣張 median） */
    minuteText: {
        2: '#3381b2',
        4: '#73aaba',
        6: '#a9d1c5',
        8: '#cce8d1',
        10: '#ecfadc',
        12: '#f5f1d6'
    }
};

/**
 * 分時人口表字體：樣張為特黑系黑體。
 * 線上以 webfont「王漢宗特黑體繁」(GPL v2，public/fonts/HanWangHeiHeavy.ttf) 為準；
 * 後備系統特黑／黑體。
 */
export const POP_TABLE_FONT_FAMILY = '王漢宗特黑體繁';
export const POP_TABLE_FONT_STACK = [
    POP_TABLE_FONT_FAMILY,
    'HanWangHeiHeavy',
    '王漢宗特黑體',
    'Lantinghei TC',
    '蘭亭黑-繁',
    'Heiti TC',
    '黑體-繁',
    'PingFang TC',
    'Microsoft JhengHei',
    'Noto Sans TC',
    'sans-serif'
].map((f) => (f === 'sans-serif' ? f : `"${f}"`)).join(',');

const SCREENSHOT_FONT_FACE_CSS = `
@font-face {
  font-family: '王漢宗特黑體繁';
  src: url('/fonts/HanWangHeiHeavy.ttf') format('truetype');
  font-weight: 100 900;
  font-style: normal;
  font-display: block;
}
@font-face {
  font-family: 'HanWangHeiHeavy';
  src: url('/fonts/HanWangHeiHeavy.ttf') format('truetype');
  font-weight: 100 900;
  font-style: normal;
  font-display: block;
}
`;

let _screenshotFontsReady = null;

/** 截圖前載入報表 webfont（失敗不擋；會落到系統後備） */
export async function ensureScreenshotFontsLoaded() {
    if (typeof document === 'undefined') return false;
    if (!_screenshotFontsReady) {
        _screenshotFontsReady = (async () => {
            if (!document.getElementById('sovereign-screenshot-fonts')) {
                const style = document.createElement('style');
                style.id = 'sovereign-screenshot-fonts';
                style.textContent = SCREENSHOT_FONT_FACE_CSS;
                document.head.appendChild(style);
            }
            try {
                if (document.fonts?.load) {
                    await Promise.all([
                        document.fonts.load(`800 48px "${POP_TABLE_FONT_FAMILY}"`),
                        document.fonts.load(`700 32px "${POP_TABLE_FONT_FAMILY}"`)
                    ]);
                }
                if (document.fonts?.ready) await document.fonts.ready;
                return !!(document.fonts?.check?.(`800 48px "${POP_TABLE_FONT_FAMILY}"`));
            } catch (err) {
                console.warn('[MapCapture] screenshot font load:', err.message);
                return false;
            }
        })();
    }
    return _screenshotFontsReady;
}

/**
 * 原生 Canvas 合成：tile → SVG → HTML 標記（POI 等）→ 圖例／浮水印
 */
export async function captureMapContainer(mapEl, options = {}) {
    if (!mapEl) throw new Error('找不到地圖容器');

    const map = options.map;
    if (map?.closePopup) map.closePopup();

    const dpr = options.devicePixelRatio ?? (window.devicePixelRatio || 1);
    const includeHtmlMarkers = options.includeHtmlMarkers !== false;
    const mapRect = mapEl.getBoundingClientRect();
    const W = Math.round(mapRect.width);
    const H = Math.round(mapRect.height);
    if (W <= 0 || H <= 0) {
        throw new Error(`地圖容器尺寸無效 (${W}×${H})，請稍後再試`);
    }

    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = W * dpr;
    outputCanvas.height = H * dpr;
    const ctx = outputCanvas.getContext('2d');
    ctx.scale(dpr, dpr);

    ctx.fillStyle = options.backgroundColor || '#f5f3f0';
    ctx.fillRect(0, 0, W, H);

    const tilePane = mapEl.querySelector('.leaflet-tile-pane');
    if (tilePane) {
        const tileImgs = Array.from(tilePane.querySelectorAll('img.leaflet-tile'));
        for (const img of tileImgs) {
            await drawTileImage(ctx, img, mapRect);
        }
    }

    const overlayPane = mapEl.querySelector('.leaflet-overlay-pane');
    const svgRoots = overlayPane
        ? Array.from(overlayPane.querySelectorAll('svg'))
        : Array.from(mapEl.querySelectorAll('.leaflet-overlay-pane svg, .leaflet-marker-pane svg'));

    for (const svg of svgRoots) {
        await drawSvgElement(ctx, svg, mapRect);
    }

    // preferCanvas／Canvas renderer：向量在 <canvas>，必須一併合成，否則只剩底圖
    const overlayCanvases = overlayPane
        ? Array.from(overlayPane.querySelectorAll('canvas'))
        : Array.from(mapEl.querySelectorAll('.leaflet-overlay-pane canvas'));
    for (const c of overlayCanvases) {
        if (!c.width || !c.height) continue;
        const cRect = c.getBoundingClientRect();
        const x = Math.round(cRect.left - mapRect.left);
        const y = Math.round(cRect.top - mapRect.top);
        const w = Math.round(cRect.width);
        const h = Math.round(cRect.height);
        if (w <= 0 || h <= 0) continue;
        try {
            ctx.drawImage(c, x, y, w, h);
        } catch (err) {
            console.warn('[MapCapture] overlay canvas skipped:', err.message);
        }
    }

    if (includeHtmlMarkers) {
        const markerCap = options.htmlMarkerCap ?? 120;
        await drawHtmlMarkers(ctx, mapEl, mapRect, dpr, markerCap);
    }

    const overlayEl = options.overlayElement;
    if (overlayEl && overlayEl.style.display !== 'none' && window.html2canvas) {
        const oCanvas = await html2canvas(overlayEl, { backgroundColor: null, scale: dpr });
        const oRect = overlayEl.getBoundingClientRect();
        ctx.drawImage(
            oCanvas,
            Math.round(oRect.left - mapRect.left),
            Math.round(oRect.top - mapRect.top),
            Math.round(oRect.width),
            Math.round(oRect.height)
        );
    }

    const legendEl = options.legendElement;
    if (legendEl && legendEl.style.display !== 'none' && window.html2canvas) {
        const lCanvas = await html2canvas(legendEl, {
            backgroundColor: options.legendBackground || 'rgba(255,255,255,0.9)',
            scale: dpr,
            useCORS: false,
            allowTaint: false
        });
        const lRect = legendEl.getBoundingClientRect();
        ctx.drawImage(
            lCanvas,
            Math.round(lRect.left - mapRect.left),
            Math.round(lRect.top - mapRect.top),
            Math.round(lRect.width),
            Math.round(lRect.height)
        );
    }

    return outputCanvas;
}

export function downloadCanvasPng(canvas, filename, options = {}) {
    const dpi = options.dpi ?? SCREENSHOT_PRINT_DPI;
    const dataUrl = canvas.toDataURL('image/png');
    const raw = dataUrl.split(',')[1] || '';
    const bin = atob(raw);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);

    let payload = bytes;
    try {
        payload = embedPngDpi(bytes, dpi);
    } catch (err) {
        console.warn('[MapCapture] PNG dpi embed skipped:', err.message);
    }

    const blob = new Blob([payload], { type: 'image/png' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = filename;
    link.href = url;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** 報表用行政區邊界（規格 #666, 2px）— 由 bbox API 載入，不依賴 window.districtLayers */
export async function fetchDistrictBoundariesGeoJSON(frameBounds, padDeg = 0.03) {
    if (!frameBounds?.isValid?.()) return null;
    const sw = frameBounds.getSouthWest();
    const ne = frameBounds.getNorthEast();
    const q = new URLSearchParams({
        xmin: String(sw.lng - padDeg),
        ymin: String(sw.lat - padDeg),
        xmax: String(ne.lng + padDeg),
        ymax: String(ne.lat + padDeg)
    });
    try {
        const res = await fetch(`/api/live/boundaries/districts?${q}`, {
            credentials: 'include',
            headers: { Accept: 'application/json' }
        });
        if (!res.ok) return null;
        const fc = await res.json();
        if (!fc?.features?.length) return null;
        return fc;
    } catch {
        return null;
    }
}

/**
 * 報表用行政區邊界（規格 #666, 2px）
 * 優先 fetch /api/live/boundaries/districts；後備 window.districtLayers／village
 * @param {{ opacity?: number }} [options] — 公車報表用 opacity 0.6（60%）
 */
export async function injectDistrictBoundariesToGhost(ghostMap, frameBounds = null, options = {}) {
    const Lref = leaflet();
    if (!ghostMap || !Lref) return 0;

    const opacity = Number.isFinite(Number(options.opacity))
        ? Math.min(1, Math.max(0, Number(options.opacity)))
        : 0.9;
    const style = { color: '#666', weight: 2, fillOpacity: 0, opacity };
    let drawn = 0;

    const fromApi = await fetchDistrictBoundariesGeoJSON(frameBounds);
    if (fromApi?.features?.length) {
        Lref.geoJSON(fromApi, { style }).addTo(ghostMap);
        drawn = fromApi.features.length;
        return drawn;
    }

    const sources = [];
    if (window.districtLayers) {
        Object.values(window.districtLayers).forEach((layer) => {
            if (layer?.toGeoJSON) sources.push(layer.toGeoJSON());
        });
    }
    const villageFeat = window.appCore?.getFeature?.('village');
    if (villageFeat?.layer?.toGeoJSON) {
        sources.push(villageFeat.layer.toGeoJSON());
    }
    if (!sources.length) return 0;

    for (const gj of sources) {
        Lref.geoJSON(gj, {
            style,
            filter: (feature) => {
                if (!feature?.geometry) return false;
                if (!frameBounds?.isValid?.()) return true;
                try {
                    const b = Lref.geoJSON(feature).getBounds();
                    return b.isValid() && frameBounds.intersects(b);
                } catch {
                    return false;
                }
            }
        }).addTo(ghostMap);
        drawn += 1;
    }
    return drawn;
}

/**
 * 截圖用：框內多區 POI 全出（優先 _screenshotPool）；依 ghost zoom 縮小圖示。
 * 知名品牌最後畫、獨立 pane、高 zIndex。
 */
export function injectPoiCabinetFeaturesToGhost(ghostMap, clipBounds = null, options = {}) {
    const Lref = leaflet();
    const create = typeof window !== 'undefined' ? window.createPOILayer : null;
    const poiCab = window.appCore?.getCabinet?.('poi');
    if (!ghostMap || !Lref || !create || !poiCab) return 0;

    if (typeof window.ensurePoiBrandPane === 'function') {
        window.ensurePoiBrandPane(ghostMap);
    }

    const checked = poiCab.shadowRoot?.querySelectorAll?.('input[type="checkbox"]:checked');
    const cats = checked?.length
        ? Array.from(checked).map((cb) => String(cb.value || '').toUpperCase()).filter(Boolean)
        : Object.keys(poiCab._screenshotPool || poiCab.categoryData || {});

    const clipPoly = options.clipPolygon || null;
    const turfRef = typeof window !== 'undefined' ? window.turf : null;
    const splitFn = typeof window.splitPoiBrandFeatures === 'function'
        ? window.splitPoiBrandFeatures
        : (features) => ({ brands: [], regular: features || [] });
    const brandZ = window.POI_BRAND_Z_OFFSET || 2500;
    const brandPane = window.POI_BRAND_PANE || 'poiBrandPane';
    const scaleFn = typeof window.poiIconScaleForZoom === 'function'
        ? window.poiIconScaleForZoom
        : () => 0.55;
    const iconScale = options.iconScale != null
        ? Number(options.iconScale)
        : scaleFn(ghostMap.getZoom?.() ?? 14);

    const inClip = (feature) => {
        const c = feature?.geometry?.coordinates;
        if (!Array.isArray(c) || c.length < 2) return false;
        const lng = Number(c[0]);
        const lat = Number(c[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
        if (clipBounds?.isValid?.() && !clipBounds.contains([lat, lng])) return false;
        if (clipPoly && turfRef?.booleanPointInPolygon) {
            try {
                return !!turfRef.booleanPointInPolygon(turfRef.point([lng, lat]), clipPoly);
            } catch {
                return true;
            }
        }
        return true;
    };

    const sourceFor = (cat) => {
        if (poiCab._screenshotPool?.[cat]) return poiCab._screenshotPool[cat];
        return poiCab.categoryData?.[cat] || [];
    };

    let n = 0;
    const regularByCat = [];
    const brandByCat = [];

    for (const cat of cats) {
        const features = sourceFor(cat).filter(inClip);
        if (!features.length) continue;
        const { brands, regular } = splitFn(features);
        if (regular?.length) regularByCat.push({ cat, features: regular });
        if (brands?.length) brandByCat.push({ cat, features: brands });
    }

    for (const { cat, features } of regularByCat) {
        create(cat, { type: 'FeatureCollection', features }, { iconScale }).addTo(ghostMap);
        n += features.length;
    }
    for (const { cat, features } of brandByCat) {
        const layer = create(cat, { type: 'FeatureCollection', features }, {
            pane: brandPane,
            zIndexOffset: brandZ,
            iconScale
        });
        layer.addTo(ghostMap);
        if (typeof layer.eachLayer === 'function') {
            layer.eachLayer((m) => {
                if (typeof m.setZIndexOffset === 'function') m.setZIndexOffset(brandZ);
                if (typeof m.bringToFront === 'function') m.bringToFront();
            });
        }
        n += features.length;
    }
    console.info(`[MapCapture] POI inject n=${n} iconScale=${iconScale.toFixed(2)} zoom=${ghostMap.getZoom?.()}`);
    return n;
}

/**
 * 從作用中地圖複製可見 overlay（略過底圖）到 ghost
 * 層序：行政區界 → 多邊形 → 線 → 標記（SOVEREIGN_ARCH_GUIDELINE §3）
 * 優先複製 Leaflet Marker／Path 實體，保留站點／POI 圖示，禁止只剩 toGeoJSON 圓點。
 */
export async function cloneLiveOverlaysToGhost(srcMap, ghostMap, options = {}) {
    const Lref = leaflet();
    if (!srcMap || !ghostMap || !Lref) return 0;
    const seen = new WeakSet();
    const skip = new Set();
    if (window.districtLayers) {
        Object.values(window.districtLayers).forEach((layer) => skip.add(layer));
    }
    // POI 改由 injectPoiCabinetFeaturesToGhost 重建，避免 cluster 少點／重複
    if (options.skipPoiLayers !== false && window.poiLayers) {
        Object.values(window.poiLayers).forEach((layer) => {
            if (layer) skip.add(layer);
        });
    }
    if (options.skipPoiLayers !== false && window.poiBrandLayer) {
        skip.add(window.poiBrandLayer);
    }

    const polygons = [];
    const lines = [];
    const markers = [];

    const walk = (layer, depth = 0) => {
        if (!layer || depth > 12 || seen.has(layer) || skip.has(layer)) return;
        seen.add(layer);
        if (layer instanceof Lref.TileLayer || layer instanceof Lref.GridLayer) return;
        if (layer instanceof Lref.CircleMarker) {
            markers.push({ kind: 'circle', layer });
            return;
        }
        if (layer instanceof Lref.Marker) {
            markers.push({ kind: 'marker', layer });
            return;
        }
        if (layer instanceof Lref.Polygon) {
            polygons.push(layer);
            return;
        }
        if (layer instanceof Lref.Polyline) {
            lines.push(layer);
            return;
        }
        if (typeof layer.eachLayer === 'function') {
            layer.eachLayer((ch) => walk(ch, depth + 1));
        }
    };

    srcMap.eachLayer((layer) => walk(layer));

    if (options.injectDistricts !== false) {
        await injectDistrictBoundariesToGhost(ghostMap, options.bounds || null, {
            opacity: options.districtOpacity
        });
    }

    if (options.injectPoiFromCabinet) {
        injectPoiCabinetFeaturesToGhost(ghostMap, options.bounds || null, {
            clipPolygon: options.clipPolygon || null
        });
    }

    const clipBounds = options.bounds?.isValid?.() ? options.bounds : null;
    const inClip = (layer) => {
        if (!clipBounds) return true;
        try {
            if (typeof layer.getLatLng === 'function') {
                return clipBounds.contains(layer.getLatLng());
            }
            if (typeof layer.getBounds === 'function') {
                const b = layer.getBounds();
                return b?.isValid?.() && clipBounds.intersects(b);
            }
        } catch {
            return true;
        }
        return true;
    };

    let n = 0;
    const fillOverride = options.polygonFillOpacity;
    for (const layer of polygons) {
        if (!inClip(layer)) continue;
        const opt = { ...(layer.options || {}) };
        if (fillOverride != null) opt.fillOpacity = fillOverride;
        if (options.solidPolygons) opt.dashArray = null;
        Lref.polygon(layer.getLatLngs(), opt).addTo(ghostMap);
        n += 1;
    }
    for (const layer of lines) {
        if (!inClip(layer)) continue;
        Lref.polyline(layer.getLatLngs(), { ...(layer.options || {}) }).addTo(ghostMap);
        n += 1;
    }
    for (const item of markers) {
        if (!inClip(item.layer)) continue;
        if (item.kind === 'circle') {
            Lref.circleMarker(item.layer.getLatLng(), { ...(item.layer.options || {}) }).addTo(ghostMap);
            n += 1;
            continue;
        }
        const layer = item.layer;
        const marker = Lref.marker(layer.getLatLng(), {
            icon: layer.options?.icon,
            opacity: layer.options?.opacity ?? 1,
            zIndexOffset: layer.options?.zIndexOffset
        }).addTo(ghostMap);
        const tt = layer.getTooltip?.();
        if (tt?.options?.permanent) {
            marker.bindTooltip(tt.getContent?.() || tt._content || '', {
                permanent: true,
                direction: tt.options.direction,
                className: tt.options.className,
                offset: tt.options.offset
            });
            marker.openTooltip();
        }
        n += 1;
    }
    return n;
}

/**
 * 將已合成 canvas 等比裝進目標紙本像素（letterbox，不拉伸）
 */
export function letterboxCanvasToSize(srcCanvas, targetW, targetH, fill = '#f5f3f0') {
    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(targetW));
    out.height = Math.max(1, Math.round(targetH));
    const ctx = out.getContext('2d');
    ctx.fillStyle = fill;
    ctx.fillRect(0, 0, out.width, out.height);
    const sw = srcCanvas.width;
    const sh = srcCanvas.height;
    if (sw <= 0 || sh <= 0) return out;
    const scale = Math.min(out.width / sw, out.height / sh);
    const dw = Math.round(sw * scale);
    const dh = Math.round(sh * scale);
    const dx = Math.round((out.width - dw) / 2);
    const dy = Math.round((out.height - dh) / 2);
    ctx.drawImage(srcCanvas, dx, dy, dw, dh);
    return out;
}

/**
 * 有分時圈時：以圈的 CSS 像素框為基準，擴成紙本長寬比後裁切（禁止整屏 letterbox 把圈外空白放大）。
 * 純幾何，便於單元測試；座標單位＝CSS 像素（與 Leaflet containerPoint 同系）。
 *
 * @param {{ x0:number, y0:number, x1:number, y1:number, canvasCssW:number, canvasCssH:number, paperAspect:number, padCss?:number }} opts
 * @returns {{ sx:number, sy:number, sw:number, sh:number } | null}
 */
export function computeIsoPaperCropRect(opts = {}) {
    const canvasCssW = Number(opts.canvasCssW);
    const canvasCssH = Number(opts.canvasCssH);
    const paperAspect = Number(opts.paperAspect);
    if (!(canvasCssW > 0) || !(canvasCssH > 0) || !(paperAspect > 0)) return null;

    const pad = Math.max(0, Number(opts.padCss) || 0);
    let x0 = Math.min(Number(opts.x0), Number(opts.x1)) - pad;
    let x1 = Math.max(Number(opts.x0), Number(opts.x1)) + pad;
    let y0 = Math.min(Number(opts.y0), Number(opts.y1)) - pad;
    let y1 = Math.max(Number(opts.y0), Number(opts.y1)) + pad;
    if (![x0, x1, y0, y1].every(Number.isFinite)) return null;

    let bw = Math.max(1, x1 - x0);
    let bh = Math.max(1, y1 - y0);
    if (bw / bh < paperAspect) {
        const need = bh * paperAspect;
        const extra = need - bw;
        x0 -= extra / 2;
        x1 += extra / 2;
        bw = need;
    } else if (bw / bh > paperAspect) {
        const need = bw / paperAspect;
        const extra = need - bh;
        y0 -= extra / 2;
        y1 += extra / 2;
        bh = need;
    }

    // 夾進畫布；夾完若長寬比仍偏，以中心再夾一邊
    x0 = Math.max(0, Math.min(x0, canvasCssW - 1));
    y0 = Math.max(0, Math.min(y0, canvasCssH - 1));
    x1 = Math.max(x0 + 1, Math.min(x1, canvasCssW));
    y1 = Math.max(y0 + 1, Math.min(y1, canvasCssH));
    bw = x1 - x0;
    bh = y1 - y0;
    const cx = (x0 + x1) / 2;
    const cy = (y0 + y1) / 2;
    if (bw / bh > paperAspect) {
        const needW = bh * paperAspect;
        x0 = Math.max(0, cx - needW / 2);
        x1 = Math.min(canvasCssW, x0 + needW);
        x0 = Math.max(0, x1 - needW);
    } else if (bw / bh < paperAspect) {
        const needH = bw / paperAspect;
        y0 = Math.max(0, cy - needH / 2);
        y1 = Math.min(canvasCssH, y0 + needH);
        y0 = Math.max(0, y1 - needH);
    }

    const sx = Math.floor(x0);
    const sy = Math.floor(y0);
    const sw = Math.max(1, Math.ceil(x1) - sx);
    const sh = Math.max(1, Math.ceil(y1) - sy);
    if (sx + sw > canvasCssW || sy + sh > canvasCssH) {
        return {
            sx: Math.max(0, Math.min(sx, canvasCssW - 1)),
            sy: Math.max(0, Math.min(sy, canvasCssH - 1)),
            sw: Math.max(1, Math.min(sw, canvasCssW - sx)),
            sh: Math.max(1, Math.min(sh, canvasCssH - sy))
        };
    }
    return { sx, sy, sw, sh };
}

/**
 * 依 Leaflet bounds 從活地圖 canvas 裁出紙本長寬比區域（CSS→canvas 像素換算）。
 */
export function cropCanvasToMapBounds(srcCanvas, map, bounds, paperW, paperH, options = {}) {
    if (!srcCanvas?.width || !map || !bounds?.isValid?.()) return null;
    const mapEl = map.getContainer?.();
    if (!mapEl) return null;
    const cssW = mapEl.clientWidth || 0;
    const cssH = mapEl.clientHeight || 0;
    if (!(cssW > 0) || !(cssH > 0)) return null;

    const nw = map.latLngToContainerPoint(bounds.getNorthWest());
    const se = map.latLngToContainerPoint(bounds.getSouthEast());
    const padArr = options.padding || [24, 24];
    const padCss = Array.isArray(padArr)
        ? Math.max(Number(padArr[0]) || 0, Number(padArr[1]) || 0)
        : Number(padArr) || 0;
    const rect = computeIsoPaperCropRect({
        x0: nw.x,
        y0: nw.y,
        x1: se.x,
        y1: se.y,
        canvasCssW: cssW,
        canvasCssH: cssH,
        paperAspect: paperW / paperH,
        padCss
    });
    if (!rect) return null;

    const scaleX = srcCanvas.width / cssW;
    const scaleY = srcCanvas.height / cssH;
    const sx = Math.round(rect.sx * scaleX);
    const sy = Math.round(rect.sy * scaleY);
    const sw = Math.max(1, Math.round(rect.sw * scaleX));
    const sh = Math.max(1, Math.round(rect.sh * scaleY));
    if (sx < 0 || sy < 0 || sx + sw > srcCanvas.width || sy + sh > srcCanvas.height) {
        return null;
    }

    const cropped = document.createElement('canvas');
    cropped.width = sw;
    cropped.height = sh;
    const ctx = cropped.getContext('2d');
    ctx.drawImage(srcCanvas, sx, sy, sw, sh, 0, 0, sw, sh);
    return cropped;
}

/**
 * POI圖快路徑：直接用活地圖已載入的圖磚／圖示 DOM（禁止重砌 ghost 重抓重畫）。
 * 有分時圈：短暫 fitBounds → 依圈裁成紙本長寬比 → 裝進 17.9×12.1；不改地圖容器尺寸。
 */
export async function captureLiveMapPrint(srcMap, options = {}) {
    if (!srcMap) throw new Error('地圖尚未就緒');
    const mapEl = srcMap.getContainer?.();
    if (!mapEl) throw new Error('找不到地圖容器');

    const dpr = options.devicePixelRatio ?? 1;
    const paperW = Math.round(options.width || 676);
    const paperH = Math.round(options.height || 457);
    const prevCenter = srcMap.getCenter();
    const prevZoom = srcMap.getZoom();
    let moved = false;
    const hasIsoBounds = !!options.bounds?.isValid?.();

    try {
        if (hasIsoBounds) {
            srcMap.fitBounds(options.bounds, {
                padding: options.padding || [24, 24],
                animate: false,
                maxZoom: options.maxZoom ?? 17
            });
            moved = true;
            await waitForMapTiles(srcMap, options.tileTimeoutMs || 2500);
            // 等候 YouBike / 停車場等視窗 moveend debounce（280ms）掛載完成
            await new Promise((r) => setTimeout(r, 350));
            await waitForPaintFrames(2);
        } else {
            await waitForPaintFrames(1);
        }

        const raw = await captureMapContainer(mapEl, {
            map: srcMap,
            includeHtmlMarkers: options.includeHtmlMarkers !== false,
            devicePixelRatio: dpr,
            htmlMarkerCap: options.htmlMarkerCap ?? 8000
        });
        if (!raw || !raw.width) {
            throw new Error('活地圖合成失敗（無畫面）');
        }

        // 有圈：以分時圈為基準裁切（活地圖長寬比≠紙本時禁止整屏 letterbox）
        let framed = raw;
        if (hasIsoBounds) {
            const cropped = cropCanvasToMapBounds(
                raw,
                srcMap,
                options.bounds,
                paperW,
                paperH,
                { padding: options.padding || [24, 24] }
            );
            if (cropped?.width) framed = cropped;
            else console.warn('[MapCapture] iso crop failed; fallback full viewport letterbox');
        }

        const out = letterboxCanvasToSize(framed, paperW * dpr, paperH * dpr);
        if (options.title) {
            const ctx = out.getContext('2d');
            const fontPx = Math.round(20 * ((options.printDpi || SCREENSHOT_PRINT_DPI) / SCREENSHOT_LAYOUT_DPI));
            ctx.save();
            ctx.fillStyle = 'rgba(15, 23, 42, 0.72)';
            ctx.font = `bold ${fontPx}px Inter,"Microsoft JhengHei",sans-serif`;
            ctx.fillText(
                String(options.title),
                Math.round(16 * (fontPx / 20) * dpr),
                Math.round(28 * (fontPx / 20) * dpr)
            );
            ctx.restore();
        }
        console.info('[MapCapture] POI live-cache capture ok', {
            raw: `${raw.width}x${raw.height}`,
            framed: `${framed.width}x${framed.height}`,
            paper: `${out.width}x${out.height}`,
            isoCrop: hasIsoBounds && framed !== raw,
            dpr
        });
        return out;
    } finally {
        if (moved) {
            srcMap.setView(prevCenter, prevZoom, { animate: false });
        }
    }
}

/**
 * 隱藏 ghost 地圖：不改活地圖尺寸。回傳 canvas。
 */
export async function captureGhostMapFromLive(srcMap, options = {}) {
    const Lref = leaflet();
    if (!srcMap || !Lref) throw new Error('地圖尚未就緒');

    const width = Math.round(options.width || 676);
    const height = Math.round(options.height || 457);
    const center = options.center || srcMap.getCenter();
    const zoom = options.zoom != null ? options.zoom : srcMap.getZoom();

    const ghostEl = document.createElement('div');
    ghostEl.id = 'screenshot-ghost';
    ghostEl.style.cssText = [
        `width:${width}px`, `height:${height}px`,
        'position:fixed', 'left:0', 'top:0', 'z-index:-9999', 'visibility:visible'
    ].join(';');
    document.body.appendChild(ghostEl);

    let ghostMap = null;
    try {
        ghostMap = Lref.map(ghostEl, {
            zoomControl: false,
            attributionControl: false,
            fadeAnimation: false,
            zoomAnimation: false,
            // 人口／POI ghost：走 SVG，避免只截到底圖
            preferCanvas: false
        });
        Lref.tileLayer(options.tileUrl || getActiveTileLayerUrl(srcMap), { maxZoom: 20 }).addTo(ghostMap);
        ghostMap.invalidateSize();
        if (options.bounds?.isValid?.()) {
            ghostMap.fitBounds(options.bounds, {
                padding: options.padding || [24, 24],
                animate: false,
                maxZoom: options.maxZoom ?? 17
            });
        } else {
            ghostMap.setView(center, zoom, { animate: false });
        }
        await cloneLiveOverlaysToGhost(srcMap, ghostMap, {
            bounds: options.bounds || ghostMap.getBounds(),
            injectDistricts: options.injectDistricts !== false,
            districtOpacity: options.districtOpacity,
            injectPoiFromCabinet: options.injectPoiFromCabinet === true,
            skipPoiLayers: options.skipPoiLayers !== false,
            polygonFillOpacity: options.polygonFillOpacity,
            solidPolygons: options.solidPolygons === true,
            clipPolygon: options.clipPolygon || null
        });
        await waitForMapTiles(ghostMap, options.tileTimeoutMs || 5000);
        await waitForPaintFrames(3);
        const canvas = await captureMapContainer(ghostEl, {
            map: ghostMap,
            overlayElement: options.overlayElement || null,
            includeHtmlMarkers: options.includeHtmlMarkers !== false,
            devicePixelRatio: options.devicePixelRatio ?? 1,
            htmlMarkerCap: options.htmlMarkerCap ?? 120
        });
        if (options.title) {
            const ctx = canvas.getContext('2d');
            const dpr = options.devicePixelRatio ?? 1;
            const fontPx = Math.round(20 * ((options.printDpi || SCREENSHOT_PRINT_DPI) / SCREENSHOT_LAYOUT_DPI));
            ctx.save();
            ctx.scale(dpr, dpr);
            ctx.fillStyle = 'rgba(15, 23, 42, 0.72)';
            ctx.font = `bold ${fontPx}px Inter,"Microsoft JhengHei",sans-serif`;
            ctx.fillText(String(options.title), Math.round(16 * (fontPx / 20)), Math.round(28 * (fontPx / 20)));
            ctx.restore();
        }
        return canvas;
    } finally {
        if (ghostMap) ghostMap.remove();
        ghostEl.remove();
    }
}

export async function waitForPaintFrames(count = 2) {
    for (let i = 0; i < count; i++) {
        await new Promise((r) => requestAnimationFrame(r));
    }
}
